import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Activity, RefreshCw, ChevronLeft, ChevronRight, Radar, Mail } from "lucide-react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import IntelMetrics from "../components/intel/IntelMetrics";
import IntelFilters from "../components/intel/IntelFilters";
import UpdateCard from "../components/intel/UpdateCard";
import WeeklyBrief from "../components/intel/WeeklyBrief";
import TimelineView from "../components/intel/TimelineView";
import { OwnerBadge, OwnerPinDialog } from "../components/intel/OwnerPin";
import DigestPreviewDialog from "../components/intel/DigestPreviewDialog";
import { SkeletonCards, EmptyState, ErrorState } from "../components/intel/IntelStates";
import { intelApi, getOwnerPin, fmtDateTime } from "../lib/intelApi";
import { useLenis } from "../hooks/useLenis";

const VIEWS = [
  { key: "feed", label: "Feed" },
  { key: "weekly", label: "This Week" },
  { key: "timeline", label: "Timeline" },
  { key: "saved", label: "Saved" },
];

const DEFAULT_FILTERS = { q: "", technology: "All", category: "All", range: "30d", date_from: "", date_to: "", state: "all", page: 1 };

function useDebounced(value, ms = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function toParams(filters, forcedSaved) {
  const p = {
    q: filters.q,
    technology: filters.technology,
    category: filters.category,
    page: filters.page,
    limit: 24,
  };
  if (filters.range === "custom") {
    p.date_from = filters.date_from;
    p.date_to = filters.date_to;
  } else {
    p.range = filters.range;
  }
  if (forcedSaved) p.saved = true;
  if (filters.state === "unread") p.read = false;
  if (filters.state === "read") p.read = true;
  if (filters.state === "high") p.min_importance = "High";
  if (filters.state === "saved") p.saved = true;
  return p;
}

export default function TechIntelligence({ theme, toggleTheme }) {
  useLenis();
  const qc = useQueryClient();
  const [view, setView] = useState("feed");
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [pinOpen, setPinOpen] = useState(false);
  const [unlocked, setUnlocked] = useState(() => !!getOwnerPin());
  const debouncedQ = useDebounced(filters.q);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Tech Intelligence — Madan Aritakula";
    return () => {
      document.title = "Madan Aritakula — Cloud Security Engineer | AWS · Azure · GCP · OCI | IAM, SIEM pipelines, CSPM";
    };
  }, []);

  const params = useMemo(() => toParams({ ...filters, q: debouncedQ }, view === "saved"), [filters, debouncedQ, view]);

  const metrics = useQuery({ queryKey: ["intel-metrics"], queryFn: intelApi.metrics, refetchInterval: 120000 });
  const weekly = useQuery({ queryKey: ["intel-weekly"], queryFn: intelApi.weekly, enabled: view === "weekly" });
  const updates = useQuery({
    queryKey: ["intel-updates", params],
    queryFn: () => intelApi.updates(params),
    enabled: view === "feed" || view === "saved",
    placeholderData: (prev) => prev,
  });

  const toggle = async (item, field) => {
    if (!unlocked) {
      setPinOpen(true);
      return;
    }
    try {
      const next = await intelApi.patch(item.id, { [field]: !item[field] });
      qc.setQueriesData({ queryKey: ["intel-updates"] }, (old) =>
        old ? { ...old, items: old.items.map((u) => (u.id === next.id ? next : u)) } : old
      );
      qc.invalidateQueries({ queryKey: ["intel-metrics"] });
      toast.success(field === "saved" ? (next.saved ? "Saved to knowledge base." : "Removed from saved.") : next.read ? "Marked as read." : "Marked as unread.");
    } catch (err) {
      if (err?.response?.status === 401) {
        setUnlocked(false);
        setPinOpen(true);
        toast.error("Owner PIN rejected. Please unlock again.");
      } else {
        toast.error("Could not update. Try again.");
      }
    }
  };

  const runIngest = async () => {
    try {
      await intelApi.runIngest();
      toast.success("Ingestion scheduled — new updates appear within a few minutes.");
    } catch (e) {
      toast.error("Could not trigger ingestion.");
    }
  };

  const [digestOpen, setDigestOpen] = useState(false);

  const changeView = (key) => {
    setView(key);
    setFilters((f) => ({ ...f, page: 1 }));
  };

  const data = updates.data;

  return (
    <div className="grain relative min-h-screen bg-background text-foreground selection:bg-accent selection:text-accent-foreground">
      <Nav theme={theme} toggleTheme={toggleTheme} />
      <main data-testid="intel-page" className="pt-28 md:pt-40 pb-24 md:pb-40">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          {/* Hero */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 mb-12 md:mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="md:col-span-3"
            >
              <div className="flex md:block items-baseline gap-3">
                <span className="font-mono text-xs md:text-sm text-accent tracking-[0.2em] uppercase">06.</span>
                <div className="mt-0 md:mt-3 font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Signal feed</div>
              </div>
              <div className="mt-6 hidden md:flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                <Activity size={12} className="text-accent" />
                Synced {fmtDateTime(metrics.data?.last_run)}
              </div>
            </motion.div>
            <div className="md:col-span-9">
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.05 }}
                className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95] uppercase"
                data-testid="intel-hero-title"
              >
                Tech Intelligence<span className="text-accent">.</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.15 }}
                className="mt-5 text-base md:text-lg text-foreground/80 max-w-[52ch]"
                data-testid="intel-hero-subtitle"
              >
                What changed. What matters. What I should know.
              </motion.p>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="mt-3 text-sm text-muted-foreground max-w-[64ch]"
              >
                Official release feeds from AWS, Google Cloud, Azure, Azure DevOps, GitHub, Ansible, Terraform and
                Kubernetes — ingested every 6 hours, deduplicated, then summarised and scored by AI against my
                cloud-security profile.
              </motion.p>
            </div>
          </div>

          <IntelMetrics data={metrics.data} loading={metrics.isLoading} />

          {/* View tabs + owner controls */}
          <div className="mt-10 md:mt-14 flex flex-wrap items-center justify-between gap-4 border-b border-border">
            <div className="flex gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="tablist" data-testid="intel-view-tabs">
              {VIEWS.map((v) => (
                <button
                  key={v.key}
                  type="button"
                  role="tab"
                  aria-selected={view === v.key}
                  data-testid={`view-tab-${v.key}`}
                  onClick={() => changeView(v.key)}
                  className={`relative shrink-0 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors ${
                    view === v.key ? "text-accent" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {v.label}
                  {view === v.key && <motion.span layoutId="intel-tab-underline" className="absolute left-3 right-3 -bottom-px h-px bg-accent" />}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 pb-2 md:pb-0">
              {unlocked && (
                <>
                  <button
                    type="button"
                    data-testid="send-digest-button"
                    onClick={() => setDigestOpen(true)}
                    className="inline-flex items-center gap-2 border border-border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:border-accent hover:text-accent transition-colors"
                  >
                    <Mail size={12} /> Email digest
                  </button>
                  <button
                    type="button"
                    data-testid="run-ingest-button"
                    onClick={runIngest}
                    className="inline-flex items-center gap-2 border border-border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:border-accent hover:text-accent transition-colors"
                  >
                    <RefreshCw size={12} /> Sync now
                  </button>
                </>
              )}
              <OwnerBadge unlocked={unlocked} onClick={() => setPinOpen(true)} />
            </div>
          </div>

          <div className="mt-8 md:mt-10">
            {view === "weekly" && <WeeklyBrief query={weekly} />}
            {view === "timeline" && <TimelineView />}
            {(view === "feed" || view === "saved") && (
              <div className="space-y-8">
                <IntelFilters filters={filters} setFilters={setFilters} showState={view === "feed"} />

                {updates.isLoading && <SkeletonCards />}
                {updates.isError && <ErrorState onRetry={updates.refetch} />}
                {data && data.items.length === 0 && (
                  <EmptyState
                    title={view === "saved" ? "Nothing saved yet" : "No updates match"}
                    body={
                      view === "saved"
                        ? "Bookmark updates from the feed to build your personal knowledge base."
                        : "Try a wider time range, another technology, or clear the search."
                    }
                    action={
                      <button
                        type="button"
                        data-testid="reset-filters-button"
                        onClick={() => setFilters(DEFAULT_FILTERS)}
                        className="border border-border px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] hover:border-accent hover:text-accent transition-colors"
                      >
                        Reset filters
                      </button>
                    }
                  />
                )}
                {data && data.items.length > 0 && (
                  <>
                    <div className="flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground" data-testid="results-count">
                      <span>
                        {data.total} updates · page {data.page} / {data.pages}
                      </span>
                      <span className="hidden sm:inline-flex items-center gap-1.5">
                        <Radar size={12} className="text-accent" /> Newest first
                      </span>
                    </div>
                    <div className={`grid grid-cols-1 lg:grid-cols-2 gap-px bg-border border border-border ${updates.isFetching ? "opacity-70" : ""} transition-opacity`} data-testid="updates-grid">
                      {data.items.map((item, i) => (
                        <UpdateCard key={item.id} item={item} index={i} onToggle={toggle} />
                      ))}
                    </div>
                    {data.pages > 1 && (
                      <div className="flex items-center justify-between" data-testid="pagination">
                        <button
                          type="button"
                          data-testid="page-prev"
                          disabled={data.page <= 1}
                          onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}
                          className="inline-flex items-center gap-2 border border-border px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] hover:border-accent hover:text-accent transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <ChevronLeft size={13} /> Prev
                        </button>
                        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                          {data.page} / {data.pages}
                        </span>
                        <button
                          type="button"
                          data-testid="page-next"
                          disabled={data.page >= data.pages}
                          onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
                          className="inline-flex items-center gap-2 border border-border px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] hover:border-accent hover:text-accent transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Next <ChevronRight size={13} />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
      <OwnerPinDialog open={pinOpen} onOpenChange={setPinOpen} onUnlocked={setUnlocked} unlocked={unlocked} />
      <DigestPreviewDialog open={digestOpen} onOpenChange={setDigestOpen} />
    </div>
  );
}
